/* eslint-disable no-use-before-define */
export interface IEntity {
  id: string;
  createdAt: string;
  updatedAt: string;
}

export enum SprintProductStatus {
  open = 'open',
  closed = 'closed',
  approved = 'approved',
  invoiced = 'invoiced',
}

export enum ProductStatus {
  deactivated = 'deactivated',
  active = 'active',
  support = 'support',
  archived = 'archived',
}

export enum MembershipStatus {
  deactivated = 'deactivated',
  active = 'active',
}

export interface DecodedJwt {
  userId: string;
  firstName: string;
  email: string;
  lastName: string;
  /** Whether the account has a password at all. Absent on tokens minted before it was added. */
  hasPassword?: boolean;
  iat: number;
  exp: number;
}

export interface SprintProductClientEntity extends IEntity {
  status: SprintProductStatus;
  sprintId?: string;
  productId?: string;
  product: ClientProductEntity;
  sprint: SprintEntity;
  clientCount?: number;
  summary?: string | null;
  mutation?: boolean;
  clients?: MembershipEntity[];
  sentBy?: UserEntity & IEntity;
  approvedBy?: UserEntity | null;
  approvedAt?: Date | null;
  /** Approved by the product's auto-approve term, so `approvedBy` is empty. */
  autoApproved?: boolean;
  // Already on the wire from SprintProductRepresentation. The client's list needs them to tell a report
  // waiting on them from one they have already disputed, which is waiting on us.
  disputedAt?: Date | null;
  comment?: string;
  disputeResolvedAt?: Date | null;
  /** Set once we answered their remark, which is what holds their approval until they read it. */
  disputeResolution?: 'dismissed' | 'updated';
}

export interface ClientProductEntity extends IEntity {
  name: string;
  status: ProductStatus;
  channelId: string;
}

export interface SprintProductEntity extends IEntity {
  status: SprintProductStatus;
  sprintId?: string;
  productId?: string;
  product?: ProductEntity;
  sprint?: SprintEntity;
  clientCount?: number;
  summary?: string;
  mutation?: boolean;
  clients?: MembershipEntity[];
  sentBy?: UserEntity;
  approvedBy?: UserEntity | null;
  approvedAt?: Date | null;
  /** Approved by the product's auto-approve term, so `approvedBy` is empty. */
  autoApproved?: boolean;
  disputedBy?: UserEntity | null;
  disputedAt?: Date | null;
  /** What the client wrote: the note on an approval, or the remark a dispute is made of. */
  comment?: string;
  /** Who the approver forwarded a copy to when approving; empty or absent if they forwarded to nobody. */
  approvalRecipients?: string[];
  /** Who the disputer sent their remark to. Kept apart from the approval's own list. */
  disputeRecipients?: string[];
  /**
   * How their last remark was answered — dismissed, or the report changed — and who answered it. Shown
   * to the client until they answer again, so a remark never just goes quiet on them.
   */
  disputeResolution?: 'dismissed' | 'updated';
  /** What was added to that answer, when anything was. Absent on a dismissal given without a word. */
  disputeResolutionNote?: string;
  disputeResolvedAt?: Date | null;
  disputeResolvedBy?: UserEntity | null;
  /** Ours: whether closing this reopened report would hand the client something different. */
  hasChangesSincePublication?: boolean;
  /**
   * Whether this report's comment is waiting on you to read it — the report's red dot, answered by the
   * server. It takes both halves of a rule the browser cannot see: whose queue the comment is in (the
   * product's managers, plus the organization's admins) and whether that person has read it. Absent for
   * a reader with no queue of their own, such as a client on their own report.
   */
  viewerHasUnreadComment?: boolean;
}

/**
 * A sprint of a product that has ended with time on it and no closed report.
 *
 * Keyed by the sprint and the product rather than by an id: the `sprint_product` row is only created when
 * somebody first opens or closes a product's sprint, so the sprints most in need of closing have no row.
 */
export interface UnclosedSprintEntity {
  sprintId: string;
  productId: string;
  productName: string;
  sprintNumber: number;
  startDate: string;
  endDate: string;
}

export interface ProductEntity extends ClientProductEntity {
  chapters: string[];
  // Only the PM products list sends these; ProductRepresentation has no managers field.
  managers?: string[];
  /** The same people by id, which is what decides whose queue an incident is in. */
  managerIds?: string[];
  membershipCount: number;
  totalMinutes: number;
  sprintProducts?: SprintProductEntity[];
  repositories: string[];
  description?: string;
  projectCode?: string;
  url?: string;
  billable?: boolean;
  // Absent means the product is not under support, so nothing can be logged against it.
  supportLevel?: SupportLevelValue | null;
  // Days a sent report waits for the client before it counts as approved. Absent means it waits for good.
  autoApproveAfterDays?: number | null;
  // The audits the client may open. Absent from an older response, which read as all of them.
  clientAuditTabs?: ClientAuditTabValue[];
  // The client the product belongs to. Both the PM list and the product itself carry it, so the column
  // and the page always name the same person.
  clientMembershipId?: string;
  clientName?: string;
  clientCompanyId?: string;
  clientCompanyName?: string;
  // Only the route that reads one product sends the team.
  team?: ProductTeamMember[];
  // Who at the client may read this product, and how many people that client has altogether.
  clientAccess?: ProductClientAccess[];
  clientPeopleCount?: number;
  // Only the route that reads one product sends this. Whether anybody has filed time against it, which is
  // what decides between offering removal and offering archiving.
  hasReportedTime?: boolean;
  // What the product is connected to, beside `repositories`, which is GitHub's link.
  aikidoRepositories?: string[];
  sentryOrgSlug?: string;
  sentryProjectSlugs?: string[];
  sentryBrowserProjectSlug?: string;
  sentryRegionHost?: string;
  /**
   * Whether this product's Sentry is read through a personal token rather than Panenco's, for a product
   * Panenco does not administer. The token itself never leaves the API.
   */
  hasExternalSentryToken?: boolean;
  sentryTokenLinkedAt?: string;
  sentryTokenLinkedByName?: string;
}

export interface ProductTeamMember {
  membershipId: string;
  firstName: string;
  // Optional, matching User.lastName — not everyone has one.
  lastName?: string;
  /** The share of this person's work on this product that counts as R&D, 0 to 100. Never an amount. */
  rndShare: number;
  isManager: boolean;
  joinedByTime?: boolean;
  /** Has reported time on this product, which keeps them on its team. */
  hasReported?: boolean;
  /** Each run of consecutive days they reported on, inclusive `YYYY-MM-DD`. */
  reportedSpans?: { from: string; to: string }[];
  /** Their default chapter while it is active, else the first they filed under: the row their rate is read off. */
  chapterKey?: string;
  seniority?: MemberSeniority | null;
  country?: MemberCountry | null;
  /** What an hour of theirs costs Panenco, in euros. Sent to admins and PMs only. */
  hourlyCost?: number;
}

/** One person's time against an incident: the same entry that makes up their day, so the two cannot drift. */
export interface IncidentWorkEntity {
  id: string;
  name: string;
  /** Whose entry it is, so a row can be opened for correction without changing who booked it. */
  userId: string;
  chapterLabel: string;
  workday: string;
  minutes: number;
  today: string;
  /** Null on entries booked before the form asked for it, which is not an error. */
  startedAt?: string | null;
  /** Whether that start falls outside the service window. Null when nobody said what time they started. */
  outOfHours?: boolean | null;
}

export interface ProductManagerCandidate {
  membershipId: string;
  firstName: string;
  // Optional, matching User.lastName and the API representation — not everyone has one.
  lastName?: string;
  isManager: boolean;
  /** Whether this person still works here. Deactivated people are listed, so a caller can tell. */
  status: MembershipStatus;
}

export interface OrganizationClient {
  membershipId: string;
  firstName: string;
  // Optional, matching User.lastName — the representation declares it required but the column is nullable.
  lastName?: string;
  email: string;
  products: string[];
}

/** A client company: who we do the work for, as the Clients screen lists them. */
export interface ClientCompany {
  id: string;
  name: string;
  /** Whether any product we do for them is still live. Decided by the API so no screen has to guess. */
  isActive: boolean;
  /** When the client was first recorded here, as an instant rather than a calendar day. */
  createdAt: string;
  /** A line about who they are. Absent until somebody writes one. */
  description?: string;
  /** Their Google Drive folder of contracts. A link: the documents stay where they are. */
  legalFolderUrl?: string;
  /** What we have signed with them. Each is a link; the documents live in the legal folder. */
  msaUrl?: string;
  sowUrl?: string;
  slaUrl?: string;
  dpaUrl?: string;
  ndaUrl?: string;
  /** Their entry on the Belgian company register. */
  companyRegisterUrl?: string;
  /** Anything else that matters: an ID to quote, a past legal case, a condition. */
  otherRequirements?: string;
  /** A renewal or deadline worth seeing coming, and what it is. */
  importantDate?: string;
  importantDateLabel?: string;
  /** When somebody last confirmed the record is correct, and who. Absent until the first review. */
  lastReviewedAt?: string;
  lastReviewedByName?: string;
  products: ClientCompanyProduct[];
  /** Only on a single client — the list leaves it out. */
  people?: ClientCompanyPerson[];
  /** Only on a single client: false once their products carry reported time. */
  canBeRemoved?: boolean;
}

/** One person at the client who can read a product, as the product's own page lists them. */
export interface ProductClientAccess {
  membershipId: string;
  firstName: string;
  lastName?: string;
  email: string;
  /** Absent on a product that belongs to no client company, which is where a standing is held. */
  role?: ClientAccessRole;
}

export interface ClientCompanyProduct {
  id: string;
  name: string;
  status?: ProductStatus;
}

export enum ClientAccessRole {
  admin = 'admin',
  read = 'read',
}

export interface ClientCompanyPerson {
  membershipId: string;
  firstName: string;
  lastName?: string;
  email: string;
  products: string[];
  /** The same products by id, which is what a change to their access has to name. */
  productIds: string[];
  /** Whether `products` is the whole of what this client has, so a row can read "All products". */
  allProducts: boolean;
  /** What they may do at this client: answer reports and manage access, or only read. */
  role: ClientAccessRole;
  /** The protected admin(s): only another super admin may change or remove them. */
  isSuperAdmin: boolean;
}

/** A client this account works with, for the panel's picker. */
export interface ClientCompanySummary {
  id: string;
  name: string;
}

export interface ClientProductOption {
  productId: string;
  name: string;
  isLinked: boolean;
}

export type UserEntity = {
  id?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  status?: MembershipStatus;
  /** Read off the access token, so it is only present on the signed-in user. */
  hasPassword?: boolean;
};
export interface MembershipEntity extends IEntity {
  role: string;
  user: UserEntity;
  slackId?: string;
  productsCount: number;
  chapters: string[];
  totalMinutes: number;
  emptyDaysCount: number;
  /** The days behind the count, as yyyy-MM-dd on the UTC calendar — the same keys `fullDate` produces. */
  emptyDays: string[];
  pingSent?: boolean;
  /** Panenco's own reference for this person. Free text that Phill only ever stores and shows. */
  memberNumber?: string | null;
  /** Where this person works from; absent until somebody records it. */
  country?: MemberCountry | null;
  /** Whether this person is a contractor rather than an employee. */
  isContractor?: boolean;
  /** The level a product prices this person at by default; absent until somebody records it. */
  seniority?: MemberSeniority | null;
  /** The chapter this person's work is filed under by default; absent until somebody chooses one. */
  defaultChapter?: MembershipDefaultChapter | null;
  /**
   * Whether this person can be removed outright. Only the single-membership read answers it; everywhere a
   * membership is nested it is absent, which reads as "do not offer removal".
   */
  canBeRemoved?: boolean;
  /** Whether Phill drafts this person's day. Their own switch and nobody else's. */
  isDailyNudgeEnabled?: boolean;
  /** When Phill's daily Slack reminder reaches them, `HH:mm` on their own clock. */
  reminderTime?: string;
  /** Monday to Friday, when they chose a time per day; null when every workday uses `reminderTime`. */
  reminderTimesByDay?: string[] | null;
}

/** The countries Panenco has people in. Mirrors the API's `Country` enum. */
export enum MemberCountry {
  belgium = 'belgium',
  ukraine = 'ukraine',
}

/** The levels a product's cost matrix prices. Mirrors the API's `Seniority` enum. */
export enum MemberSeniority {
  intern = 'intern',
  student = 'student',
  junior = 'junior',
  middle = 'middle',
  senior = 'senior',
}

/** One value the money tabs keep for a product, serialised as the tab wrote it. */
export interface ProductStoreEntry {
  key: string;
  value: string;
  updatedAt: string;
}

/** Private to one person, so it is served from its own route rather than on the membership. */
export interface DraftSources {
  draftReadsGithub: boolean;
  draftReadsSlack: boolean;
  draftReadsCalendar: boolean;
}

export interface AiSummaryInstructions extends Partial<DraftSources> {
  aiSummaryInstructions?: string;
}

export interface HourlyCost {
  hourlyCost?: number;
}

/** What the API nests on a membership: a chapter row, without ChapterRepresentation's legacy aliases. */
export type MembershipDefaultChapter = Pick<Chapter, 'id' | 'key' | 'label' | 'isActive'>;

/** Adding a colleague: a name and an email is all Phill needs to let somebody in. */
export interface MembershipCreateValues {
  firstName: string;
  lastName?: string;
  email: string;
  role?: string;
}

/**
 * One person's page saves through a single request that spans both records behind it — the membership's
 * own fields plus the person's name and email. Every field is optional: only what changed is sent.
 */
export interface MembershipUpdateValues {
  role?: string;
  memberNumber?: string | null;
  country?: MemberCountry | null;
  isContractor?: boolean;
  seniority?: MemberSeniority | null;
  defaultChapter?: string | null;
  firstName?: string;
  lastName?: string;
  email?: string;
}
export interface InvolvementEntity extends IEntity {
  role: string;
  product: ProductEntity;
  membership: MembershipEntity;
}
export interface ReportEntity extends IEntity {
  today: string;
  minutes: number;
  threadId: string;
  workday: Date;
  involvement: InvolvementEntity;
  userId?: string;
  hours: number;
  role: string;
  /** The sprint product covering this report's workday; anything but `open` means the server refuses edits. */
  sprintProductStatus?: SprintProductStatus;
  /** The incident this time went on, when it went on one. Absent for ordinary work. */
  incidentId?: string;
  /** When the work started. Only carried on incident work, where the hour decides the rate. */
  startedAt?: string;
}

/** A report already sent to the client cannot be edited or deleted — `checkProduct` refuses both. */
export const isReportLocked = (report: ReportEntity): boolean =>
  !!report.sprintProductStatus && report.sprintProductStatus !== SprintProductStatus.open;

export interface SprintEntity extends IEntity {
  startDate: string;
  endDate: string;
  number: number;
}

export interface WorkDayUserEntity {
  date: string;
  totalMinutes: number;
  reports: ReportEntity[];
  /** Narrowed to some products, so `totalMinutes` is not the whole day. */
  productsFiltered?: boolean;
}

/** Time off from Officient that the evening run will file, unless the day is filled first. */
export interface PendingAbsenceEntity {
  workday: string;
  productName: string;
  minutes: number;
}

export interface WorkDayChapter {
  name: string;
  reports: ReportEntity[];
  totalMinutes: number;
}
export interface WorkDayProduct {
  date: string;
  chapters: WorkDayChapter[];
  totalMinutes: number;
}

export interface SprintProductMember {
  member: MembershipEntity;
  totalMinutes: number;
}

export interface SprintProductChapter {
  chapter: string;
  totalMinutes: number;
  members: SprintProductMember[];
}

export interface RoleEntity {
  role: string;
  text: string;
  isUserDefault: boolean;
  isMostRecentInvolvement: boolean;
}

export type IError = {
  detail: string;
  status?: number;
  title?: string;
  type?: string;
};

export type ApiStore = {
  error: null | IError;
  success: null | { id: string; message: string };
  refreshFailed?: boolean;
};

/** What the Phill report form opens with — see GET /organizations/:id/reports/daily-defaults. */
export interface DailyReportDefaults {
  /** Chapter key the report will be filed under; absent when the organization has no active chapters. */
  chapter?: string;
  /** The chapter they chose in their settings, with none of `chapter`'s guesses under it. */
  chosenChapter?: string;
  /** Products reported on the most recent reported day, to open the picker already ticked. */
  products: string[];
  /** Open products whose team this person is on, most reported in the last four weeks first. */
  teamProducts: string[];
}

export interface OrganizationEntity extends IEntity {
  name: string;
  memberships: MembershipEntity[];
  role: string;
  slackTeamInfo?: any;
  logoUrl?: string;
}

export interface Chapter {
  id: string;
  key: string;
  label: string;
  isActive: boolean;
  /** Creation order among the organization's chapters, which picks its --chapter-n colour. Only on the list. */
  colorSlot?: number;
  /** People whose own chapter (Settings → General → "Your chapter") is this one. */
  /** Absent for client accounts, which do not receive the organization's headcount. */
  memberCount?: number;
  // Legacy aliases kept while the FE finishes migrating off role/text (#41)
  role: string;
  text: string;
}

export interface CreateChapterValues {
  key: string;
  label: string;
}

export interface UpdateChapterValues {
  label?: string;
  isActive?: boolean;
}

export type OrganizationParamsType = {
  organizationId: string;
};

export type SprintProductParamsType = {
  organizationId: string;
  sprintId: string;
  productId: string;
};

export type OrganizationFormValues = {
  name: string;
};

export type SendSprintFormValues = {
  sprintProductId?: string;
  emails: string[];
  /** Our own people, copied in on every client's mail. */
  cc?: string[];
  /** False leaves the sender off the copy line; absent copies them in. */
  copySender?: boolean;
  /** The mail as the sender left it. Absent sends the draft the API composes. */
  subject?: string;
  blocks?: MailBlock[];
  /** The sprint's price as the app works it out, for the mail's cost row. */
  totalCost?: number;
};

export type MailAmountRow = { label: string; amount: string };

/** One block of an editable mail body: a paragraph, a heading or a table of amounts. */
export type MailBlock =
  | { kind: 'text'; text: string }
  | { kind: 'heading'; text: string }
  | { kind: 'amounts'; rows: MailAmountRow[]; totals: MailAmountRow[] };

/** The sprint report mail before it is sent, rendered by the API's own template. */
export interface SprintReportMailDraft {
  subject: string;
  /** Everything between the greeting and the buttons. The greeting is per recipient. */
  blocks: MailBlock[];
  /** The rendered mail, the blocks marked `data-mail-slot="message"`. */
  html: string;
}

export type ApprovalRecipient = { email: string; name: string };

export type ApprovalRecipients = { colleagues: ApprovalRecipient[]; managers: ApprovalRecipient[] };

export type ApproveSprintFormValues = {
  sprintProductId?: string;
  approvalRecipients: string[];
  sprintId: string;
  productId: string;
  comment?: string;
};

export type RevokeDisputeValues = {
  sprintProductId?: string;
  sprintId: string;
  productId: string;
  /** Why the report stands, when whoever dismissed it wrote one. Optional, as the field is. */
  note?: string;
};

export type DisputeSprintFormValues = {
  sprintProductId?: string;
  disputeRecipients: string[];
  sprintId: string;
  productId: string;
  /** Required: a dispute is the remark, so there is nothing to send without one. */
  remark: string;
};

export type ListResponse<TItem> = {
  count: number;
  items: TItem[];
};

export type Product = {
  count: number;
  items: SprintEntity[];
};

export type OrganizationSlackAuthorizeValues = {
  code: string | null;
  redirectUri?: string;
};

export interface PmInfiniteData {
  from: string | Date;
  to: string | Date;
  sprintNumber: number;
  sprintId: string;
}

export interface PmMembersInfiniteData extends PmInfiniteData {
  items: MembershipEntity[];
}

export interface PmProductsInfiniteData extends PmInfiniteData {
  items: ProductEntity[];
}

export interface SprintProductReportsData {
  detailed: WorkDayProduct[] | null;
  brief: SprintProductChapter[] | null;
}

export type PmParams = {
  from?: string | Date;
  to?: string | Date;
  sprintNumber?: number;
  sprintId?: string;
  statuses?: ProductStatus[];
  search?: string;
  unreadOnly?: boolean;
};

export interface PmMembersParams extends PmParams {
  userStatus?: MembershipStatus;
}

export type SprintProductReportsParams = {
  sprint?: SprintEntity;
  productId: string;
  chapters?: string[];
  // Set when one of us reads the product page: these entries as the client reads them, frozen as sent.
  asClient?: boolean;
  // The sprint's incident work instead of its report, which leaves incident time out.
  incidentWork?: boolean;
};

export type ProductParams = { statuses?: ProductStatus[]; teamMembershipId?: string };

export interface PmProductsParams {
  from?: string;
  to?: string;
  productIds?: string[];
  statuses?: ProductStatus[];
  search?: string;
}

/** How far a pasted Sentry token turned out to reach. Reported back when one is linked. */
export interface SentryAccess {
  organizations: number;
  projects: number;
}

/** One Sentry project the configured token can see, with what it actually carries. */
export interface SentryProject {
  orgSlug: string;
  slug: string;
  name: string;
  platform?: string;
  sessionCount: number;
  transactionCount: number;
}

/** One repository Aikido scans. `lastScannedAt` is null for one it holds but has never scanned. */
export interface AikidoRepository {
  fullName: string;
  lastScannedAt?: string | null;
}

/** One repository the organisation owns, for the GitHub picker. */
export interface GithubRepository {
  fullName: string;
  language?: string | null;
  isArchived: boolean;
}

/** The picker's list, with the accounts the API could not read: a shorter list on its own says nothing. */
export interface GithubRepositoryListing extends ListResponse<GithubRepository> {
  failedAccounts: string[];
}

// Adding a product asks for the same things its page shows, and writes the team in the same request.
export interface CreateProduct {
  name: string;
  status: ProductStatus;
  description?: string;
  projectCode?: string;
  url?: string;
  billable?: boolean;
  // The client COMPANY the product is done for. `clientMembershipId` is the older per-person read
  // access, still accepted by the API and no longer sent by any screen.
  clientCompanyId?: string;
  clientMembershipId?: string;
  team?: { membershipId: string; rndShare?: number; isManager?: boolean }[];
}

export interface RndDossier {
  context?: string;
  existingKnowledge?: string;
  advancement?: string;
  obstacles?: string;
  activities?: string;
  academicCollaboration?: boolean;
  // Whole percent. Absent means nobody has said yet, which is not 0%.
  rndShare?: number;
}

export interface UpdateProduct {
  name?: string;
  status?: ProductStatus;
  repositories?: string[];
  description?: string;
  projectCode?: string | null;
  url?: string | null;
  billable?: boolean;
  // Null takes the product off support, which is a real answer and not the same as leaving it out.
  supportLevel?: SupportLevelValue | null;
  // Null turns auto-approval off.
  autoApproveAfterDays?: number | null;
  clientAuditTabs?: ClientAuditTabValue[];
  // The client COMPANY the product is done for; null clears it. `clientMembershipId` is the older
  // per-person read access, still accepted by the API and no longer sent by any screen.
  clientCompanyId?: string | null;
  clientMembershipId?: string | null;
  // Sent alongside the fields, because the card saves both behind one button.
  team?: { membershipId: string; rndShare?: number; isManager?: boolean }[];
  // What the product is connected to. Each is set on its own, and null disconnects it.
  aikidoRepositories?: string[];
  sentryOrgSlug?: string | null;
  sentryProjectSlugs?: string[] | null;
  sentryBrowserProjectSlug?: string | null;
}

/**
 * A product on the client's report list, carrying the report it stands for. One card can hold several
 * products of the same sprint, and each is approved or disputed on its own, so the state has to travel
 * per product rather than being read off whichever one landed on the card first.
 */
export type ClientReportProductEntity = ClientProductEntity & { report: SprintProductClientEntity };

export type ClientReportWithProductsEntity = SprintProductClientEntity & { products: ClientReportProductEntity[] };

export enum RoleType {
  BE = 'BE',
  FE = 'FE',
  PM = 'PM',
  C = 'C',
  DS = 'DS',
  G = 'G',
  S = 'S',
}

export type AddReportFormValues = {
  userId: string;
  productId: string;
  role: string;
  today: string;
  customHours: number | null;
  hours: number | null;
  workday: Date;
};

/**
 * The five health readings a product's dashboard shows, and its quality audits.
 *
 * Every one of them answers "nothing is known" with a null and says so in its own comment, because the
 * same mistake is available in all five: a client reads a zero as a clean bill of health. The API
 * returns facts only — what turns these into good, watch or risk lives in the dashboard's thresholds.
 */
export interface ResponseTimePoint {
  date: string;
  /** Null on a day with no transactions — a gap in the line, not a 0ms response. */
  p75Ms: number | null;
}

/**
 * `p75Ms: null` means Sentry has nothing for this product, which is not the same as a fast product.
 * The card must show an unknown state, because a client reads "0 ms" as perfect speed.
 */
export interface ProductResponseTime {
  p75Ms: number | null;
  transactionCount: number;
  series: ResponseTimePoint[];
  /** How far back the series reaches, capped by Sentry's 90-day event retention. */
  windowDays: number;
}

/**
 * `crashFreeRatePct: null` means Sentry has no usable release health for the product — no sessions, or
 * too few for a percentage to mean anything. The card shows an unknown state rather than a number.
 */
export interface ProductCrashFree {
  crashFreeRatePct: number | null;
  sessionCount: number;
  /** One point a day; `ratePct` is null on a day with too few sessions to mean anything. */
  series: { date: string; ratePct: number | null }[];
  windowDays: number;
}

/** One dependency as the API reports it: facts only, no judgement. */
export interface ProductDependency {
  name: string;
  ecosystem: string;
  versionInUse: string;
  /** Null when the registry has no record — normal for private packages. */
  latestVersion: string | null;
  /** When the version IN USE was published. */
  releasedAt: string | null;
  /** When the CURRENT release was published. */
  latestReleasedAt: string | null;
  majorsBehind: number | null;
  /** Deprecation on npm is per version: this is the version in use. */
  isDeprecated: boolean;
  /** The current release is deprecated too, so the whole package is. */
  isLatestDeprecated: boolean;
  deprecatedReason: string | null;
  /** When the first stable release newer than the version in use came out. Null when nothing newer is known. */
  behindSince: string | null;
  /** When the first stable release of a newer line came out: a newer major, or for 0.x a newer first non-zero segment. */
  nextMajorSince: string | null;
  /** The latest stable release on the line in use (the major, or for 0.x the first non-zero segment, as 0.48 or 0.0.3). */
  lineLastReleasedAt: string | null;
  /** Distinct versions this package is a direct dependency at; `versionInUse` is the oldest. */
  directVersionCount: number;
}

/**
 * `isTracked: false` means no inventory covers this product, which is NOT the same as nothing being
 * outdated. The card shows unknown, or a client reads a clean donut as a clean bill of health.
 *
 * `isUnreachable` separates "we could not look today" from "there is nothing to look at": the first is
 * temporary and the second is not, and the card words them differently.
 */
export interface ProductDependencies {
  isTracked: boolean;
  isUnreachable: boolean;
  dependencies: ProductDependency[];
  /** `owner/name` of the linked repositories left out because they are archived on GitHub. */
  excludedArchived: string[];
  /** Every linked repository is archived, which the card words apart from one nobody connected. */
  isAllArchived: boolean;
  /** The oldest registry read behind these figures. */
  checkedAt: string | null;
  /** Direct packages that could not be checked: no resolved version, or unknown to the registry. */
  uncheckedCount: number;
}

export interface ProductSecurityCheck {
  /** Aikido's own issue type, e.g. open_source, sast, leaked_secret, iac. */
  type: string;
  issues: number;
}

/**
 * `isScanned: false` means nobody has looked, which is NOT the same as a product with nothing open. The
 * card shows unknown, or a client reads 0 open issues as a clean bill of health.
 */
export interface ProductSecurity {
  isScanned: boolean;
  critical: number;
  high: number;
  medium: number;
  low: number;
  checks: ProductSecurityCheck[];
  /** Worst first. */
  issues: ProductSecurityIssue[];
  lastScannedAt: string | null;
  /** `owner/name` of the linked repositories left out because they are archived on GitHub. */
  excludedArchived: string[];
  /** Every linked repository is archived, which the card words apart from one nobody connected. */
  isAllArchived: boolean;
}

/** Whether attackers are known to use it; Aikido fills it in mainly for dependencies. */
export type SecurityExploitability = 'actively_exploited' | 'poc_exists' | 'no_known_exploit';

/** One open Aikido issue group. `locations` is empty for a client, who is told only how many `places`. */
export interface ProductSecurityIssue {
  id: number;
  severity: 'critical' | 'high' | 'medium' | 'low';
  type: string;
  title: string;
  howToFix?: string | null;
  minutesToFix?: number | null;
  firstDetectedAt?: string | null;
  cveIds: string[];
  exploitability?: SecurityExploitability | null;
  package?: { name: string; installedVersion?: string | null; fixedIn?: string | null } | null;
  places: number;
  locations: { file: string; line?: number | null }[];
}

/**
 * One quarter's quality audit, as the quality-audit skill published it.
 *
 * `audit` is deliberately loose: the checklist grows between versions and the web only renders it, so
 * pinning its shape here would mean a type change every time the skill learns a new check. The Quality
 * tab's own module owns the render-time shape.
 */
export interface ProductQualityAudit {
  id: string;
  year: number;
  quarter: number;
  generatedOn: string;
  /** Only filled in by the single-audit endpoint; the index leaves it out. */
  audit?: Record<string, unknown>;
}

/** One quarter's productivity report; `report` is the API's built view, and the Productivity tab's module owns its shape. */
export interface ProductProductivityReport {
  id: string;
  year: number;
  quarter: number;
  /** The day the measurement ends. */
  generatedOn: string;
  /** Only filled in by the single-report endpoint; the index leaves it out. */
  report?: Record<string, unknown>;
}

/** The reports a product's repository can carry beside its quality audit, each a whole page. */
export type AuditReportKind = 'productivity' | 'security';

export interface ProductAuditReport {
  kind: AuditReportKind;
  /** Changes exactly when the committed report does. */
  sha: string;
  /** Only filled in by the single-report endpoint; the index leaves it out. */
  html?: string;
}

/** One of the client's own products, as their product list shows it. */
export interface ClientProductSummary {
  id: string;
  name: string;
  status?: ProductStatus;
  /** The product's address, which its page links to. */
  url?: string | null;
}

/** Minutes one person reported on a product in one sprint, under one chapter. */
export interface ClientProductTimeEntry {
  membershipId: string;
  chapterKey: string;
  chapterLabel: string;
  totalMinutes: number;
}

/**
 * One sprint's reported time on a product — a bar of the client's delivery chart. Sprints nobody
 * reported on come back with no entries rather than being skipped, so the series keeps its gaps.
 */
export interface ClientProductSprintTime {
  sprintId: string;
  number: number;
  startDate: string;
  endDate: string;
  entries: ClientProductTimeEntry[];
}

/** The activity sources Phill can be given permission to read. */
export type PhilBotApp = 'github' | 'googleCalendar' | 'slack';

export type PhillConnections = Record<PhilBotApp, boolean>;

/** The support agreement level a product is on. Absent means it is not under support at all. */
export type SupportLevelValue = 'standard' | 'bronze' | 'silver' | 'gold';

/** An audit a product can show its client; the security one opens on the `security-audit` tab. */
export type ClientAuditTabValue = 'quality' | 'security' | 'productivity';

export type IncidentPriorityValue = 'p1' | 'p2' | 'p3' | 'p4';

export type IncidentChannelValue = 'email' | 'slack' | 'phone' | 'phill';

/** One incident as the API sends it. */
export interface IncidentEntity {
  id: string;
  productId: string;
  productName: string;
  /** Null once the product is taken off support, which is not the same as the incident being gone. */
  supportLevel: SupportLevelValue | null;
  /** The short name it is known by, and all a table or a picker has room for. */
  title: string;
  /** The write-up, absent until somebody writes one. Read on the incident's own page. */
  description?: string;
  priority: IncidentPriorityValue;
  channel?: IncidentChannelValue;
  reportedAt: string;
  respondedAt?: string | null;
  resolvedAt?: string | null;
  /** Absent while the record is still open. A different question from whether the thing is fixed. */
  closedAt?: string | null;
  /** Null while nobody has broken the time down, which is not a confirmed zero. */
  hours: number | null;
  loggedByName?: string;
  /** The stretches nobody could work through. Empty on nearly every incident. */
  pauses?: IncidentPauseEntity[];
}

/** One stretch of an incident spent waiting on somebody else. `endedAt` is null while it is still on. */
export interface IncidentPauseEntity {
  id: string;
  startedAt: string;
  endedAt?: string | null;
  reason?: string | null;
}

/** Opening a pause, or writing down one that is already over. */
export interface IncidentPauseInput {
  startedAt: string;
  endedAt?: string | null;
  reason?: string;
}

/** Why a product x sprint pair produces no report. Mirrors the API's own two answers. */
export type IncidentReportRefusal = 'notReportable' | 'nothingToReport';

/** Step 1's answer for one pair: whether it will produce a file, and if not, why not. */
export interface IncidentReportAvailability {
  productId: string;
  sprintId: string;
  available: boolean;
  reason?: IncidentReportRefusal;
}

/** A product's tagged manager, as someone the reports can be sent to. */
export interface ProductManager {
  email: string;
  name: string;
  firstName?: string;
}

export interface IncidentReportRecipientInput {
  email: string;
  /** Only used for the mail's greeting; absent for an address typed in by hand. */
  firstName?: string;
  /** Copied in on the client's own mail rather than sent one of their own. */
  cc?: boolean;
}

export interface IncidentReportSendInput {
  productIds: string[];
  sprintIds: string[];
  recipients: IncidentReportRecipientInput[];
  /** The mail as the sender left it. Absent sends the draft the API composes. */
  subject?: string;
  message?: string;
}

/** The mail a selection would send, before it is sent: what the send step shows the sender. */
export interface IncidentReportDraft {
  subject: string;
  /** Everything between the greeting and the frame, as text. The greeting is per recipient. */
  message: string;
  /** The mail rendered by the API's own template, message marked `data-mail-slot="message"`. */
  html: string;
  files: string[];
}

export interface IncidentReportSendResult {
  /** False when the environment has mail switched off, so the screen can say so instead of lying. */
  dispatched: boolean;
  /** Mails sent: one per addressee, so each can be greeted by name. */
  mails: number;
  /** People copied in on those mails rather than addressed by them. */
  copied: number;
  files: string[];
  skipped: string[];
}

export interface IncidentInput {
  title: string;
  /** Empty clears it, which is how a write-up nobody stands behind comes off. */
  description?: string;
  priority: IncidentPriorityValue;
  channel?: IncidentChannelValue;
  reportedAt: string;
  respondedAt?: string | null;
  resolvedAt?: string | null;
  hours?: number;
}

/** Every field optional: absent means "leave it alone", so one dialog can send one timestamp. */
export type IncidentPatch = Partial<IncidentInput> & { reviewed?: boolean };

/** Closing an incident. The moment it was fixed, sent only for one that has none recorded yet. */
export interface IncidentClosure {
  resolvedAt?: string | null;
}

/** Where a product's budget workbook is, and when Phill last wrote to it. */
export interface BudgetSheetLink {
  spreadsheetId: string;
  url: string;
  syncedAt?: string;
}

/** One row of a people block: who they are, what their hour costs, and a figure per sprint. */
export interface BudgetSheetPerson {
  name: string;
  country?: string;
  level?: string;
  rate?: number | null;
  /** One per sprint, in the order the sprints run: what the hours in that sprint are priced at. */
  rates?: number[];
  hours: number[];
}

/** Sent on every sync because the budget lives in the browser; it goes when it gets tables. */
export interface BudgetSheetPayload {
  year: number;
  productName: string;
  sprints: { number: number; startDate: string; isClosed: boolean }[];
  groups: { label: string; people: BudgetSheetPerson[] }[];
  fractional: BudgetSheetPerson[];
  outOfPocket: { section: string; name: string; quarters: number[] }[];
  rateCard: { chapter: string; level: string; belgium?: number | null; ukraine?: number | null }[];
  service?: {
    from: string;
    to: string;
    days: number;
    targets: { priority: string; response: number; resolution: number }[];
  };
}
